/**
 * Shared visual language for the Evaluation workspace.
 *
 * Deliberately narrow: a page shell, a step rail, panels, and the small
 * primitives (bands, confidence, citations, meters) that repeat across both
 * flows. Everything reads from the app theme so the workspace stays visually
 * continuous with the rest of the platform.
 */
import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { theme } from '../../styles/theme';
import { Check, FileText, ChevronRight } from 'lucide-react';

/* ---------------------------------------------------------------- shell -- */

export const Shell = styled.div`
  max-width: 1360px;
  margin: 0 auto;
  padding-bottom: ${theme.spacing[10]};
`;

export const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${theme.colors.primary[500]};
  margin-bottom: ${theme.spacing[3]};

  &::before {
    content: '';
    width: 6px; height: 6px; border-radius: 50%;
    background: ${theme.colors.primary[500]};
  }
`;

export const PageTitle = styled.h1`
  font-family: ${theme.typography.fontFamily.display};
  font-size: clamp(1.75rem, 2.4vw, 2.25rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[2]};
`;

export const PageSub = styled.p`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.base};
  max-width: 62ch;
  margin: 0;
  line-height: 1.6;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing[6]};
  flex-wrap: wrap;
  margin-bottom: ${theme.spacing[6]};
`;

/* -------------------------------------------------------- context strip -- */

export const ContextBar = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 18px;
  padding: ${theme.spacing[5]} ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};
`;

export const ContextGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  /* No dividers: an item that wraps to a second row would keep its rule and
     read as indented. The labels carry the structure on their own. */
  gap: ${theme.spacing[5]} ${theme.spacing[6]};
  row-gap: ${theme.spacing[4]};
`;

const CtxLabel = styled.div`
  font-size: 10px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  margin-bottom: 5px;
  font-weight: 700;
`;

const CtxValue = styled.div<{ $mono?: boolean }>`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  color: ${theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;
  line-height: 1.4;
  ${p => p.$mono && css`
    font-family: ${theme.typography.fontFamily.mono};
    font-size: 12px;
    color: ${theme.colors.primary[500]};
  `}
`;

export const ContextItem: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <div>
    <CtxLabel>{label}</CtxLabel>
    <CtxValue $mono={mono}>{value}</CtxValue>
  </div>
);

/* --------------------------------------------------------------- steps --- */

export const StepRail = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
  margin-bottom: ${theme.spacing[6]};
  border: 1px solid ${theme.colors.border};
  border-radius: 14px;
  background: ${theme.colors.surface};
  overflow: hidden;
`;

const StepCell = styled.button<{ $state: 'done' | 'active' | 'todo' }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[3]};
  border: 0;
  border-right: 1px solid ${theme.colors.border};
  background: ${p => p.$state === 'active' ? theme.colors.primary[50] : theme.colors.surface};
  cursor: ${p => p.$state === 'todo' ? 'default' : 'pointer'};
  text-align: left;
  min-width: 0;
  transition: background 160ms ease;

  &:last-child { border-right: 0; }
  &:hover { background: ${p => p.$state === 'todo' ? theme.colors.surface : theme.colors.primary[50]}; }
`;

const StepDot = styled.span<{ $state: 'done' | 'active' | 'todo' }>`
  flex: 0 0 auto;
  width: 22px; height: 22px;
  border-radius: 50%;
  display: grid; place-items: center;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  ${p => p.$state === 'done' && css`background: ${theme.colors.success[500]}; color: #fff;`}
  ${p => p.$state === 'active' && css`background: ${theme.colors.primary[500]}; color: #fff;`}
  ${p => p.$state === 'todo' && css`background: ${theme.colors.backgroundTertiary}; color: ${theme.colors.textDisabled};`}
`;

const StepText = styled.span<{ $state: 'done' | 'active' | 'todo' }>`
  font-size: 12.5px;
  font-weight: ${p => p.$state === 'active' ? 700 : 500};
  color: ${p => p.$state === 'todo' ? theme.colors.textDisabled : p.$state === 'active' ? theme.colors.primary[500] : theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Steps: React.FC<{ labels: string[]; current: number; onJump?: (i: number) => void }> = ({ labels, current, onJump }) => (
  <StepRail>
    {labels.map((l, i) => {
      const state = i < current ? 'done' : i === current ? 'active' : 'todo';
      return (
        <StepCell key={l} $state={state} onClick={() => state !== 'todo' && onJump?.(i)}>
          <StepDot $state={state}>{state === 'done' ? <Check size={12} strokeWidth={3} /> : i + 1}</StepDot>
          <StepText $state={state}>{l}</StepText>
        </StepCell>
      );
    })}
  </StepRail>
);

/* -------------------------------------------------------------- panels --- */

export const Panel = styled.section`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 16px;
  margin-bottom: ${theme.spacing[5]};
  overflow: hidden;
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.backgroundSecondary};
  flex-wrap: wrap;
`;

export const PanelTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

export const PanelNote = styled.p`
  font-size: 12.5px;
  color: ${theme.colors.textMuted};
  margin: 3px 0 0;
`;

export const PanelBody = styled.div<{ $flush?: boolean }>`
  padding: ${p => p.$flush ? '0' : `${theme.spacing[5]}`};
`;

export const Split = styled.div<{ $ratio?: string }>`
  display: grid;
  grid-template-columns: ${p => p.$ratio || '1fr 1fr'};
  align-items: start;
  gap: ${theme.spacing[5]};
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

/* ------------------------------------------------------------ primitives -- */

export const MicroLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  margin-bottom: 6px;
`;

export const Mono = styled.span`
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 11.5px;
  color: ${theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

export const Num = styled.span<{ $size?: string; $weight?: number }>`
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  font-size: ${p => p.$size || 'inherit'};
  font-weight: ${p => p.$weight || 600};
  color: ${theme.colors.textPrimary};
`;

const TileWrap = styled.div<{ $accent?: boolean }>`
  border: 1px solid ${p => p.$accent ? theme.colors.primary[200] : theme.colors.border};
  background: ${p => p.$accent ? theme.colors.primary[50] : theme.colors.surface};
  border-radius: 12px;
  padding: ${theme.spacing[4]};
`;

const TileVal = styled.div`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 1.6rem;
  font-weight: 700;
  color: ${theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  line-height: 1.1;
`;

const TileFoot = styled.div`
  font-size: 11.5px;
  color: ${theme.colors.textMuted};
  margin-top: 4px;
`;

export const StatTile: React.FC<{ label: string; value: React.ReactNode; foot?: React.ReactNode; accent?: boolean }> = ({ label, value, foot, accent }) => (
  <TileWrap $accent={accent}>
    <MicroLabel>{label}</MicroLabel>
    <TileVal>{value}</TileVal>
    {foot && <TileFoot>{foot}</TileFoot>}
  </TileWrap>
);

export const TileRow = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${p => p.$cols || 4}, 1fr);
  gap: ${theme.spacing[3]};
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;

/* Band pill — the 0–4 assessment score. */
const BAND_COLOR: Record<number, { bg: string; fg: string; bd: string }> = {
  4: { bg: '#DCFCE7', fg: '#15803D', bd: '#86EFAC' },
  3: { bg: '#E1E1FD', fg: '#000052', bd: '#C8C8FB' },
  2: { bg: '#FEF3C7', fg: '#A16207', bd: '#FDE68A' },
  1: { bg: '#FFEDD5', fg: '#C2410C', bd: '#FED7AA' },
  0: { bg: '#FEE2E2', fg: '#B91C1C', bd: '#FECACA' },
};

export const BandPill = styled.span<{ $band: number; $lg?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${p => p.$lg ? '34px' : '26px'};
  height: ${p => p.$lg ? '30px' : '22px'};
  padding: 0 8px;
  border-radius: 7px;
  font-size: ${p => p.$lg ? '14px' : '12px'};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: ${p => BAND_COLOR[p.$band]?.bg};
  color: ${p => BAND_COLOR[p.$band]?.fg};
  border: 1px solid ${p => BAND_COLOR[p.$band]?.bd};
`;

export const Tag = styled.span<{ $tone?: 'ok' | 'warn' | 'bad' | 'muted' | 'info' }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  ${p => {
    switch (p.$tone) {
      case 'ok': return css`background:${theme.colors.success[100]};color:${theme.colors.success[700]};border:1px solid #86EFAC;`;
      case 'warn': return css`background:${theme.colors.warning[100]};color:${theme.colors.warning[700]};border:1px solid #FDE68A;`;
      case 'bad': return css`background:${theme.colors.error[100]};color:${theme.colors.error[700]};border:1px solid #FECACA;`;
      case 'info': return css`background:${theme.colors.primary[50]};color:${theme.colors.primary[500]};border:1px solid ${theme.colors.primary[200]};`;
      default: return css`background:${theme.colors.backgroundTertiary};color:${theme.colors.textMuted};border:1px solid ${theme.colors.border};`;
    }
  }}
`;

/* Confidence — a small bar plus the figure. Reads at a glance in a dense table. */
const ConfWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-variant-numeric: tabular-nums;
  font-size: 11.5px;
  color: ${theme.colors.textMuted};
`;
const ConfTrack = styled.span`
  width: 38px; height: 4px; border-radius: 2px;
  background: ${theme.colors.backgroundTertiary};
  overflow: hidden;
`;
const ConfFill = styled.span<{ $v: number }>`
  display: block; height: 100%;
  width: ${p => Math.round(p.$v * 100)}%;
  background: ${p => p.$v >= 0.9 ? theme.colors.primary[400] : p.$v >= 0.8 ? '#8A8ADB' : theme.colors.warning[500]};
`;

export const Confidence: React.FC<{ value: number }> = ({ value }) => (
  <ConfWrap title={`Model confidence ${(value * 100).toFixed(0)}%`}>
    <ConfTrack><ConfFill $v={value} /></ConfTrack>
    {(value * 100).toFixed(0)}%
  </ConfWrap>
);

/* Citation chip — doc, clause, page. The anchor that makes a score defensible. */
const CiteChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.backgroundSecondary};
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 10.5px;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  transition: all 140ms ease;
  &:hover { border-color: ${theme.colors.primary[300]}; background: ${theme.colors.primary[50]}; color: ${theme.colors.primary[500]}; }
`;

export const Cite: React.FC<{ doc: string; clause: string; page: number; onClick?: () => void }> = ({ doc, clause, page, onClick }) => (
  <CiteChip onClick={onClick} title={doc} role={onClick ? 'button' : undefined}>
    <FileText size={10} />
    {clause} · p.{page}
  </CiteChip>
);

export const Quote = styled.blockquote`
  margin: ${theme.spacing[2]} 0;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-left: 3px solid ${theme.colors.primary[300]};
  background: ${theme.colors.backgroundSecondary};
  border-radius: 0 8px 8px 0;
  font-size: 12.5px;
  line-height: 1.65;
  color: ${theme.colors.textSecondary};
  font-style: italic;
`;

export const CiteFoot = styled.div`
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 10.5px;
  color: ${theme.colors.textMuted};
  margin-top: 6px;
  font-style: normal;
`;

/* Weight meter — a stacked proportional bar. */
const MeterTrack = styled.div`
  display: flex;
  height: 10px;
  border-radius: 6px;
  overflow: hidden;
  background: ${theme.colors.backgroundTertiary};
  border: 1px solid ${theme.colors.border};
`;
const MeterSeg = styled.div<{ $w: number; $c: string }>`
  width: ${p => p.$w}%;
  background: ${p => p.$c};
  transition: width 320ms cubic-bezier(.4,0,.2,1);
`;

export const SEG_COLORS = ['#000052', '#2D2D8F', '#5B5BC4', '#8A8ADB', '#F59E0B'];

export const Meter: React.FC<{ segments: { label: string; value: number; color?: string }[] }> = ({ segments }) => {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  return (
    <MeterTrack>
      {segments.map((s, i) => (
        <MeterSeg key={s.label} $w={(s.value / total) * 100} $c={s.color || SEG_COLORS[i % SEG_COLORS.length]} title={`${s.label} — ${s.value}%`} />
      ))}
    </MeterTrack>
  );
};

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[3]};
  margin-top: ${theme.spacing[3]};
`;

export const LegendItem = styled.span<{ $c: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: ${theme.colors.textSecondary};
  font-variant-numeric: tabular-nums;
  &::before {
    content: '';
    width: 9px; height: 9px; border-radius: 3px;
    background: ${p => p.$c};
  }
`;

/* Horizontal score bar used in ranking / comparison views. */
const ScoreTrack = styled.div`
  flex: 1;
  height: 22px;
  border-radius: 6px;
  background: ${theme.colors.backgroundTertiary};
  overflow: hidden;
  display: flex;
  min-width: 80px;
`;
const ScorePart = styled.div<{ $w: number; $c: string }>`
  width: ${p => p.$w}%;
  background: ${p => p.$c};
  transition: width 420ms cubic-bezier(.4,0,.2,1);
`;

export const ScoreBar: React.FC<{ quality: number; price: number; max?: number }> = ({ quality, price, max = 100 }) => (
  <ScoreTrack>
    <ScorePart $w={(quality / max) * 100} $c="#000052" title={`Quality ${quality.toFixed(2)}`} />
    <ScorePart $w={(price / max) * 100} $c="#F59E0B" title={`Price ${price.toFixed(2)}`} />
  </ScoreTrack>
);

/* --------------------------------------------------------------- table --- */

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.11em;
    text-transform: uppercase;
    color: ${theme.colors.textMuted};
    border-bottom: 1px solid ${theme.colors.border};
    background: ${theme.colors.backgroundSecondary};
    white-space: nowrap;
  }
  td {
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    border-bottom: 1px solid ${theme.colors.borderLight};
    color: ${theme.colors.textSecondary};
    vertical-align: middle;
  }
  tbody tr:last-child td { border-bottom: 0; }
  tbody tr:hover td { background: ${theme.colors.backgroundSecondary}; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
`;

export const TableScroll = styled.div`
  overflow-x: auto;
`;

/* --------------------------------------------------------------- misc ---- */

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
  padding-top: ${theme.spacing[2]};
`;

export const Spacer = styled.div`flex: 1;`;

export const Callout = styled.div<{ $tone?: 'ok' | 'warn' | 'bad' | 'info' }>`
  display: flex;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.6;
  ${p => {
    switch (p.$tone) {
      case 'ok': return css`background:${theme.colors.success[100]};border:1px solid #86EFAC;color:${theme.colors.success[700]};`;
      case 'warn': return css`background:${theme.colors.warning[100]};border:1px solid #FDE68A;color:${theme.colors.warning[700]};`;
      case 'bad': return css`background:${theme.colors.error[100]};border:1px solid #FECACA;color:${theme.colors.error[700]};`;
      default: return css`background:${theme.colors.primary[50]};border:1px solid ${theme.colors.primary[200]};color:${theme.colors.primary[500]};`;
    }
  }}
  strong { font-weight: 700; }
`;

const shimmer = keyframes`
  0% { opacity: .35 } 50% { opacity: 1 } 100% { opacity: .35 }
`;

export const Pulse = styled.span`
  display: inline-block;
  width: 7px; height: 7px; border-radius: 50%;
  background: ${theme.colors.success[500]};
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

export const Chevron = styled(ChevronRight)<{ $open?: boolean }>`
  transition: transform 180ms ease;
  transform: rotate(${p => p.$open ? 90 : 0}deg);
  flex: 0 0 auto;
`;

/* ------------------------------------------------------------ processing -- */

const spin = keyframes`to { transform: rotate(360deg) }`;

const ProcWrap = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 16px;
  padding: ${theme.spacing[8]} ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ProcRing = styled.div`
  width: 34px; height: 34px;
  border-radius: 50%;
  border: 3px solid ${theme.colors.backgroundTertiary};
  border-top-color: ${theme.colors.primary[500]};
  animation: ${spin} 780ms linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @media (prefers-reduced-motion: reduce) { animation-duration: 2.4s; }
`;

const ProcTitle = styled.div`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[4]};
`;

const ProcList = styled.ul`
  list-style: none;
  margin: 0; padding: 0;
  display: grid;
  gap: 9px;
  text-align: left;
  min-width: min(420px, 100%);
`;

const ProcItem = styled.li<{ $done: boolean }>`
  display: flex; align-items: center; gap: 9px;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  color: ${p => (p.$done ? theme.colors.textSecondary : theme.colors.textDisabled)};
  transition: color 220ms ease;
`;

const ProcDot = styled.span<{ $done: boolean }>`
  flex: 0 0 auto;
  width: 15px; height: 15px; border-radius: 50%;
  display: grid; place-items: center;
  border: 1.5px solid ${p => (p.$done ? theme.colors.success[500] : theme.colors.border)};
  background: ${p => (p.$done ? theme.colors.success[500] : 'transparent')};
  color: #fff;
  transition: all 220ms ease;
`;

/**
 * Short processing stage for a step that derives its output rather than
 * fetching it. Lines tick off in sequence so the wait reads as work being
 * done, not as a spinner parked on the screen.
 */
export const Processing: React.FC<{ title: string; lines: string[]; duration?: number }> = ({
  title, lines, duration = 1900,
}) => {
  const [elapsed, setElapsed] = React.useState(0);

  React.useEffect(() => {
    const started = performance.now();
    let raf = 0;
    const tick = () => {
      const e = performance.now() - started;
      setElapsed(e);
      if (e < duration) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [duration]);

  const per = duration / (lines.length + 0.6);

  return (
    <ProcWrap role="status" aria-live="polite">
      <ProcRing />
      <ProcTitle>{title}</ProcTitle>
      <ProcList>
        {lines.map((l, i) => {
          const done = elapsed > per * (i + 1);
          return (
            <ProcItem key={l} $done={done}>
              <ProcDot $done={done}>{done && <Check size={9} strokeWidth={4} />}</ProcDot>
              {l}
            </ProcItem>
          );
        })}
      </ProcList>
    </ProcWrap>
  );
};
